import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { Questionnaire } from '../../types/questionnaire';
import Button from '../ui/Button';

interface QuestionnaireFormProps {
  questionnaire: Questionnaire;
  onSubmit: (responses: Record<string, boolean | string>) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const QuestionnaireForm: React.FC<QuestionnaireFormProps> = ({
  questionnaire,
  onSubmit,
  onCancel,
  isSubmitting = false
}) => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [showHelp, setShowHelp] = useState<string | null>(null);

  // Scroll automatiquement vers le haut lors de l'affichage du questionnaire
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const watchedValues = watch();

  const onFormSubmit = async (data: any) => {
    // Convertir les strings en booléens
    const convertedData = Object.keys(data).reduce((acc, key) => {
      acc[key] = data[key] === 'true';
      return acc;
    }, {} as Record<string, boolean>);
    
    await onSubmit(convertedData);
  };

  const getQuestionIcon = (questionId: string) => {
    const value = watchedValues[questionId];
    if (value === undefined || value === '') return null;
    
    // Pas d'icône colorée pour ne pas influencer le client
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <AlertTriangle className="text-orange-600" size={32} />
          </div>
          <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-2">
            {questionnaire.title}
          </h2>
          <p className="text-neutral-body max-w-2xl mx-auto">
            {questionnaire.description}
          </p>
        </div>

        {/* Avertissement */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-8">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-orange-800 mb-1">Important</h3>
              <p className="text-orange-700 text-sm">
                Ce questionnaire permet de vérifier votre éligibilité au service sélectionné. 
                Répondez honnêtement à toutes les questions pour votre sécurité.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Questions */}
          <div className="space-y-6">
            {(questionnaire.questions || []).map((question, index) => (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-neutral-line rounded-xl p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-accent-rose text-brand rounded-full text-sm font-bold">
                        {index + 1}
                      </span>
                      <h3 className="font-playfair font-semibold text-neutral-ink">
                        {question.text}
                      </h3>
                      {question.required && (
                        <span className="text-red-500 text-sm">*</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {getQuestionIcon(question.id)}
                    <button
                      type="button"
                      onClick={() => setShowHelp(showHelp === question.id ? null : question.id)}
                      className="text-neutral-body hover:text-neutral-ink transition-colors"
                    >
                      <HelpCircle size={20} />
                    </button>
                  </div>
                </div>

                {/* Aide contextuelle */}
                {showHelp === question.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-blue-50 rounded-lg p-3 mb-4"
                  >
                    <p className="text-blue-700 text-sm">
                      Cette question permet d'identifier d'éventuelles contre-indications au service.
                      En cas de doute, consultez votre médecin.
                    </p>
                  </motion.div>
                )}

                {/* Champ de réponse */}
                <div className="ml-11">
                  {question.type === 'boolean' ? (
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          value="false"
                          {...register(question.id, { 
                            required: 'Cette question est obligatoire',
                            setValueAs: (value) => value === 'true'
                          })}
                          className="w-4 h-4 text-brand border-neutral-line focus:ring-brand"
                        />
                        <span className="text-neutral-body font-medium">Non</span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          value="true"
                          {...register(question.id, { 
                            required: 'Cette question est obligatoire',
                            setValueAs: (value) => value === 'true'
                          })}
                          className="w-4 h-4 text-brand border-neutral-line focus:ring-brand"
                        />
                        <span className="text-neutral-body font-medium">Oui</span>
                      </label>
                    </div>
                  ) : question.type === 'select' && question.options ? (
                    <select
                      {...register(question.id, { 
                        required: question.required ? 'Cette question est obligatoire' : false 
                      })}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                    >
                      <option value="">Sélectionnez une option</option>
                      {question.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <textarea
                      {...register(question.id, { 
                        required: question.required ? 'Cette question est obligatoire' : false 
                      })}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                      rows={3}
                      placeholder="Votre réponse..."
                    />
                  )}
                  
                  {errors[question.id] && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors[question.id]?.message as string}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Information de sécurité */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Attention</h3>
                <p className="text-red-700 text-sm">
                  Si vous répondez "Oui" à l'une des questions, cela peut constituer une contre-indication 
                  au service. Dans ce cas, nous vous recommandons de consulter votre médecin avant de procéder.
                </p>
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            {onCancel && (
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                className="flex-1"
              >
                Annuler
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Validation en cours...' : 'Valider le questionnaire'}
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default QuestionnaireForm;
